// src/utils/voiceProfile.js
// The brand-voice profile format stored in public.brand_voices.voice_text, and the
// normalization the app applies before saving it. Moved unchanged from profile.js so
// it is free of React, Vite and Supabase and can be tested in plain Node; profile.js
// re-exports it for existing callers.

const DEFAULT_NEGATIVE_PROMPTS = [
  "Do not sound like a generic AI assistant.",
  "Do not use corporate buzzwords or startup-speak.",
  "Do not use fake enthusiasm, fake urgency, or motivational-poster language.",
  "Do not become more playful or more snarky than requested.",
  "Do not force humor, sass, or personality into every line.",
  "Do not over-polish the writing into something stiff or unnatural.",
];

const DEFAULT_STYLE_RULES = [
  "Prefer direct, plainspoken wording.",
  "Specific beats generic.",
  "Sound human, grounded, and experienced.",
  "Use humor only when it fits naturally.",
  "Clarity comes before cleverness.",
];

const DEFAULT_BANNED_PHRASES = [
  "unlock",
  "unleash",
  "game-changer",
  "elevate your",
  "delve into",
  "in today's fast-paced world",
  "level up",
  "revolutionary",
];

function clamp01(value, fallback = 0.5) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function uniqClean(list) {
  return [...new Set((Array.isArray(list) ? list : [])
    .map((x) => String(x || "").trim())
    .filter(Boolean))];
}

function splitLines(value) {
  return String(value || "")
    .split(/\r?\n|,/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function makeDefaultVoiceProfile(summary = "", snarkLevel = 0.3) {
  return {
    __dobiecore_voice_v1: true,
    voice_name: "",
    summary: String(summary || "").trim(),
    playfulness_level: 0.35,
    snark_level: clamp01(snarkLevel, 0.3),
    warmth_level: 0.65,
    formality_level: 0.55,
    emoji_tolerance: "low",
    cta_style: "direct",
    style_rules: [...DEFAULT_STYLE_RULES],
    negative_prompt: [...DEFAULT_NEGATIVE_PROMPTS],
    banned_phrases: [...DEFAULT_BANNED_PHRASES],
    sample_phrases: [],
    avoid_phrases: [],
    helper_notes: {},
    source_confidence: "manual",
  };
}

export function normalizeVoiceProfile(input, fallbackSnark = 0.3) {
  if (!input) return makeDefaultVoiceProfile("", fallbackSnark);

  if (typeof input === "string") {
    try {
      const parsed = JSON.parse(input);
      if (parsed && typeof parsed === "object") {
        input = parsed;
      } else {
        return makeDefaultVoiceProfile(input, fallbackSnark);
      }
    } catch {
      return makeDefaultVoiceProfile(input, fallbackSnark);
    }
  }

  const raw = typeof input === "object" ? input : {};

  const profile = {
    __dobiecore_voice_v1: true,
    voice_name: String(raw.voice_name || raw.name || "").trim(),
    summary: String(
      raw.summary || raw.brand_voice || raw.voice_text || raw.text || ""
    ).trim(),
    playfulness_level: clamp01(raw.playfulness_level, 0.35),
    snark_level: clamp01(raw.snark_level, fallbackSnark),
    warmth_level: clamp01(raw.warmth_level, 0.65),
    formality_level: clamp01(raw.formality_level, 0.55),
    emoji_tolerance: String(raw.emoji_tolerance || "low").trim() || "low",
    cta_style: String(raw.cta_style || "direct").trim() || "direct",
    style_rules: uniqClean(
      Array.isArray(raw.style_rules) ? raw.style_rules : splitLines(raw.style_rules)
    ),
    negative_prompt: uniqClean(
      Array.isArray(raw.negative_prompt)
        ? raw.negative_prompt
        : splitLines(raw.negative_prompt)
    ),
    banned_phrases: uniqClean(
      Array.isArray(raw.banned_phrases)
        ? raw.banned_phrases
        : splitLines(raw.banned_phrases)
    ),
    sample_phrases: uniqClean(
      Array.isArray(raw.sample_phrases)
        ? raw.sample_phrases
        : splitLines(raw.sample_phrases)
    ),
    avoid_phrases: uniqClean(
      Array.isArray(raw.avoid_phrases)
        ? raw.avoid_phrases
        : splitLines(raw.avoid_phrases)
    ),
    helper_notes:
      raw.helper_notes && typeof raw.helper_notes === "object"
        ? raw.helper_notes
        : {},
    source_confidence: String(raw.source_confidence || "manual").trim() || "manual",
  };

  if (!profile.style_rules.length) {
    profile.style_rules = [...DEFAULT_STYLE_RULES];
  }
  if (!profile.negative_prompt.length) {
    profile.negative_prompt = [...DEFAULT_NEGATIVE_PROMPTS];
  }
  if (!profile.banned_phrases.length) {
    profile.banned_phrases = [...DEFAULT_BANNED_PHRASES];
  }

  return profile;
}

export function serializeVoiceProfile(profile) {
  return JSON.stringify(normalizeVoiceProfile(profile));
}
